import { FilledInputProps, InputProps, OutlinedInputProps, TextField, TextFieldProps } from "@mui/material";
import React, { FormEvent, KeyboardEvent, WheelEvent, forwardRef, useMemo } from "react";

interface INumberInputRestProps {
	isContactNumber?: boolean;
	positiveOnly?: boolean;
	disableSigned?: boolean;
	disableDecimal?: boolean;
	password?: boolean;
	maxLength?: number;
	minValue?: number;
	maxValue?: number;
}

type NumberInputPropsType = INumberInputRestProps & TextFieldProps;

type TextFieldInputPropsType =
	| Partial<FilledInputProps>
	| Partial<OutlinedInputProps>
	| Partial<InputProps>
	| undefined;

const NumberInput = forwardRef<HTMLInputElement, NumberInputPropsType>((props, ref) => {
	const {
		isContactNumber,
		positiveOnly,
		disableSigned,
		disableDecimal,
		password,
		maxLength,
		minValue,
		maxValue,
		InputProps,
		...restProps
	} = props;

	const TextFieldInputProps = useMemo<TextFieldInputPropsType>(() => {
		const newInputProps: TextFieldInputPropsType = { ...InputProps };

		if (positiveOnly) newInputProps.inputProps = { ...newInputProps.inputProps, min: 0 };
		if (typeof minValue === "number") newInputProps.inputProps = { ...newInputProps.inputProps, min: minValue };
		if (typeof maxValue === "number") newInputProps.inputProps = { ...newInputProps.inputProps, max: maxValue };

		return newInputProps;
	}, [InputProps, maxValue, minValue, positiveOnly]);

	function handleWheelEvent(event: WheelEvent<HTMLDivElement>) {
		event.currentTarget.querySelector("input")?.blur();
	}

	function handleKeyDownEvent(event: KeyboardEvent<HTMLDivElement>) {
		const checkPreventDefault = (key: string): boolean => {
			if (key === "e" || key === "E") return true;
			if ((key === "-" || key === "+") && (isContactNumber || disableSigned)) return true;
			if (key === "." && (isContactNumber || disableDecimal)) return true;

			return false;
		};

		if (checkPreventDefault(event.key)) event.preventDefault();
	}

	function handleInputEvent(event: FormEvent<HTMLDivElement>) {
		const inputElement = event.target as HTMLInputElement;

		if (isContactNumber) inputElement.value = Math.max(0, parseInt(inputElement.value)).toString().slice(0, 10);
		if (password) inputElement.value = inputElement.value.replace(/\D/g, "");
		if (typeof maxLength === "number") inputElement.value = inputElement.value.slice(0, maxLength);

		if (positiveOnly) {
			const inputValue = parseInt(inputElement.value) || 0;
			if (inputValue < 0) inputElement.value = "0";
		}

		if (typeof minValue === "number") {
			const inputValue = parseInt(inputElement.value);
			if (inputValue < minValue) inputElement.value = minValue.toString();
		}

		if (typeof maxValue === "number") {
			const inputValue = parseInt(inputElement.value);
			if (inputValue > maxValue) inputElement.value = maxValue.toString();
		}
	}

	return (
		<TextField
			ref={ref}
			type={password ? "password" : "number"}
			InputProps={TextFieldInputProps}
			onInput={handleInputEvent}
			onWheel={handleWheelEvent}
			onKeyDown={handleKeyDownEvent}
			autoComplete="off"
			{...restProps}
		/>
	);
});

export default NumberInput;
